// Вкладка «Досье»: карточка персонажа, загрузка портрета, цели,
// настройки пола, согласие на аватар. Вынесено из CampaignSetupPage.tsx
// без изменения поведения (Фаза 1).

import { useRef } from "react";
import { api, type CampaignSetup, type SetupMember } from "../api";
import {
  MemberAvatar,
  ParagraphText,
  ProgressBar,
  type DossierSection,
  type PageAction,
} from "./shared";
import { AssetBadge } from "./worldAssets";

function PlayerGenderSettings({
  member,
  action,
}: {
  member: SetupMember;
  action: PageAction;
}) {
  const options: Array<{ value: SetupMember["playerGender"]; label: string; hint: string }> = [
    { value: "UNSPECIFIED", label: "Не указан", hint: "Игра будет использовать нейтральные формулировки через должность." },
    { value: "MALE", label: "Мужской", hint: "Повествование и изображения будут согласованы в мужском роде." },
    { value: "FEMALE", label: "Женский", hint: "Повествование и изображения будут согласованы в женском роде." },
  ];
  return (
    <div className="cc-player-gender-card">
      <strong>Пол персонажа</strong>
      <p>Telegram обычно не передаёт пол профиля, поэтому значение можно задать вручную. Оно влияет на род в летописи, действиях и промптах изображений.</p>
      <div className="cc-player-gender-options">
        {options.map((option) => (
          <button
            type="button"
            className={member.playerGender === option.value ? "active" : ""}
            aria-pressed={member.playerGender === option.value}
            key={option.value}
            onClick={() =>
              void action("Обновляем пол персонажа…", () => api.updatePlayerGender(option.value))
            }
          >
            <span>{option.label}</span>
            <small>{option.hint}</small>
          </button>
        ))}
      </div>
    </div>
  );
}
function AvatarGenerationConsent({
  campaignId,
  member,
  action,
}: {
  campaignId: string;
  member: SetupMember;
  action: (label: string, run: () => Promise<unknown>) => Promise<boolean>;
}) {
  const generation = member.avatarGeneration;
  return (
    <div className="cc-avatar-generation-card">
      <label className="cc-avatar-generation-toggle">
        <input
          type="checkbox"
          checked={generation.enabled}
          disabled={!generation.available}
          onChange={(event) =>
            void action(
              event.target.checked
                ? "Включаем использование аватара в генерации…"
                : "Отключаем использование аватара в генерации…",
              () => api.updateAvatarGeneration(campaignId, event.target.checked),
            )
          }
        />
        <span>
          <strong>Использовать мой аватар в иллюстрациях действий</strong>
          <small>
            Игра будет просить генератор делать персонажа в ваших действиях похожим на профильное изображение, но в общем стиле хроники.
          </small>
        </span>
      </label>
      <p>{generation.hint}</p>
      {generation.source !== "CUSTOM_SELFIE" && (
        <em>Для лучшего результата загрузите селфи, где хорошо видно лицо.</em>
      )}
    </div>
  );
}
function DossierTab({
  campaignId,
  setup,
  section,
  action,
}: {
  campaignId: string;
  setup: CampaignSetup;
  section: DossierSection;
  action: PageAction;
}) {
  const profile = setup.myPrivateProfile;
  const currentMember = setup.members.find((member) => member.isCurrentUser);
  const character = currentMember
    ? setup.characters.find(
        (item) => item.campaignMemberId === currentMember.campaignMemberId,
      )
    : undefined;
  if (!profile || !currentMember)
    return (
      <section className="cc-block">
        <p>Личное досье пока не сформировано.</p>
      </section>
    );

  const cycle = setup.cycle.current;
  const myActions = cycle?.actions.filter(
    (item) => item.actorUserId === currentMember.userId,
  ).length ?? 0;
  const myAgreements = cycle?.agreements.filter((item) =>
    item.participantUserIds.includes(currentMember.userId),
  ).length ?? 0;
  const goal = profile.personalGoalProgress;
  const roleTitle = character?.publicTitle ??
    (currentMember.memberRole === "OWNER" ? "Организатор кампании" : "Участник Совета");

  if (section === "intro") {
    return (
      <div className="cc-dossier-profile-screen">
        <section className="cc-dossier-hero-card">
          <DossierPortraitUploader
            campaignId={campaignId}
            member={currentMember}
            action={action}
          />
          <div className="cc-dossier-identity-copy">
            <span>Персонаж</span>
            <h2>{currentMember.displayName}</h2>
            <p>{roleTitle}</p>
          </div>
        </section>

        <section className="cc-dossier-strip cc-dossier-power-strip">
          <span>Полномочие</span>
          <p>{character?.specialPower ?? "Полномочие будет назначено после создания мира."}</p>
        </section>

        <div className="cc-dossier-rows">
          <button type="button" className="cc-dossier-row" onClick={() => undefined}>
            <span>Мои действия</span>
            <b>{myActions} действий</b>
          </button>
          <button type="button" className="cc-dossier-row" onClick={() => undefined}>
            <span>Мои договорённости</span>
            <b>{myAgreements} активные</b>
          </button>
        </div>

        <section className="cc-dossier-strip">
          <span>Публичная роль</span>
          <p>{character?.publicDuties ?? profile.personalIntroduction}</p>
        </section>

        <section className="cc-dossier-strip cc-dossier-goal-strip">
          <span>Личная цель</span>
          <p>{goal?.description ?? profile.personalGoal}</p>
          {goal && (
            <div className="cc-dossier-mini-progress">
              <ProgressBar value={goal.progressPercent} />
              <b>{goal.progressPercent}%</b>
            </div>
          )}
        </section>

        <section className="cc-dossier-strip cc-dossier-loyalty-strip">
          <span>Скрытая лояльность</span>
          <p>{profile.hiddenLoyalty?.title ?? profile.hiddenMotiveTitle ?? "Скрытый мотив"}</p>
          {profile.hiddenLoyalty?.description && (
            <small>{profile.hiddenLoyalty.description}</small>
          )}
        </section>

        <section className="cc-dossier-settings-panel">
          <span>Настройки</span>
          <PlayerGenderSettings member={currentMember} action={action} />
          <AvatarGenerationConsent
            campaignId={campaignId}
            member={currentMember}
            action={action}
          />
        </section>
      </div>
    );
  }

  if (section === "goal") {
    if (!goal)
      return (
        <article className="cc-dossier-card priority cc-dossier-single">
          <AssetBadge kind="goal" label="Личная цель" />
          <span>Главная задача</span>
          <h2>Личная цель эпохи</h2>
          <ActionText text={profile.personalGoal} />
        </article>
      );
    return (
      <article className="cc-dossier-card priority cc-dossier-single cc-personal-goal">
        <AssetBadge kind="goal" label="Личная цель" />
        <span>
          {goal.status === "COMPLETED"
            ? "Цель выполнена"
            : "Личная цель кампании"}
        </span>
        <h2>{goal.title}</h2>
        <ActionText text={goal.description} />
        <div className="cc-personal-goal-progress">
          <div>
            <b>Прогресс</b>
            <strong>{goal.progressPercent}%</strong>
          </div>
          <ProgressBar value={goal.progressPercent} />
        </div>
        <div className="cc-goal-stages">
          {goal.stages.map((stage) => (
            <article className={stage.status.toLowerCase()} key={stage.id}>
              <i>{stage.status === "COMPLETED" ? "✓" : stage.order}</i>
              <div>
                <small>{goalStageStatusLabel(stage.status)}</small>
                <h3>{stage.title}</h3>
                <p>{stage.description}</p>
              </div>
            </article>
          ))}
        </div>
        {goal.recentProgress.length > 0 && (
          <details className="cc-goal-history">
            <summary>Последние изменения прогресса</summary>
            {goal.recentProgress.map((event) => (
              <p key={event.id}>
                <b>+{event.progressDelta}%</b> {event.narrativeText}
              </p>
            ))}
          </details>
        )}
      </article>
    );
  }

  return (
    <div className="cc-dossier-stack">
      <article className="cc-dossier-card cc-dossier-single">
        <AssetBadge kind="dossier" label="Вступление в хронику" />
        <span>Персональное начало</span>
        <h2>Вступление в хронику</h2>
        <ParagraphText text={profile.personalIntroduction} />
      </article>
      {setup.cycle.current?.privateBriefing && (
        <article className="cc-dossier-card cc-dossier-single cc-daily-briefing-dossier">
          <AssetBadge kind="dossier" label="Служебная сводка дня" />
          <span>Только для вас · текущий день</span>
          <h2>Служебная сводка дня</h2>
          <ParagraphText text={setup.cycle.current.privateBriefing} />
          <p className="sharing-guidance">
            Вы сами решаете, сохранить сведения в тайне или раскрыть их полностью либо частично.
          </p>
        </article>
      )}
      <article className="cc-dossier-card cc-dossier-single">
        <AssetBadge kind="dossier" label="Служебная информация" />
        <span>Закрытые сведения</span>
        <h2>Служебная информация</h2>
        <ParagraphText text={profile.privateBriefing} />
        {profile.briefingSharingGuidance && (
          <p className="sharing-guidance">
            <strong>Можно ли обсуждать?</strong> {profile.briefingSharingGuidance}
          </p>
        )}
      </article>
      <article className="cc-dossier-card cc-dossier-single">
        <AssetBadge kind="dossier" label="Скрытый мотив" />
        <span>Только для вас</span>
        <h2>{profile.hiddenMotiveTitle ?? "Скрытый мотив"}</h2>
        <ParagraphText text={profile.hiddenMotiveDescription} />
      </article>
    </div>
  );
}
function DossierPortraitUploader({
  campaignId,
  member,
  action,
}: {
  campaignId: string;
  member: SetupMember;
  action: PageAction;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  async function choose(file?: File) {
    if (!file) return;
    await action("Загружаем фото и ставим ИИ-стилизацию в очередь…", async () => {
      if (!["image/jpeg", "image/png"].includes(file.type))
        throw new Error("Поддерживаются только JPEG и PNG");
      if (file.size > 5 * 1024 * 1024)
        throw new Error("Размер изображения не должен превышать 5 МБ");
      return api.uploadCampaignAvatar(campaignId, file);
    });
    if (inputRef.current) inputRef.current.value = "";
  }
  const styleStatus = member.avatar.styleStatus ?? "NONE";
  const statusText =
    styleStatus === "READY"
      ? "Портрет стилизован"
      : styleStatus === "QUEUED"
        ? "ИИ-стилизация готовится"
        : styleStatus === "FAILED"
          ? "Стилизация не удалась, можно загрузить фото ещё раз"
          : "Нажмите на портрет, чтобы загрузить селфи";
  return (
    <div className="cc-dossier-photo-wrap">
      <button
        type="button"
        className="cc-dossier-photo-button"
        onClick={() => inputRef.current?.click()}
        aria-label="Загрузить фото профиля"
      >
        <MemberAvatar campaignId={campaignId} member={member} xlarge />
        <span>Сменить фото</span>
      </button>
      <small className={`cc-dossier-photo-status ${styleStatus.toLowerCase()}`}>
        {statusText}
      </small>
      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/jpeg,image/png"
        onChange={(event) => void choose(event.target.files?.[0])}
      />
    </div>
  );
}
function goalStageStatusLabel(status: "LOCKED" | "ACTIVE" | "COMPLETED") {
  return (
    {
      LOCKED: "Ещё не открыт",
      ACTIVE: "Текущий этап",
      COMPLETED: "Выполнено",
    } as const
  )[status];
}
function ActionText({ text }: { text?: string }) {
  const normalized = text?.trim() ?? "";
  if (!normalized) return null;
  const match = normalized.match(/^([А-ЯЁA-Zа-яёa-z-]+)([\s\S]*)$/);
  if (!match) return <p className="action-text">{normalized}</p>;
  return (
    <p className="action-text">
      <strong>{match[1].toUpperCase()}</strong>
      {match[2]}
    </p>
  );
}

export {
  DossierTab,
  DossierPortraitUploader,
  goalStageStatusLabel,
  PlayerGenderSettings,
  AvatarGenerationConsent,
  ActionText,
};
